import { CancelExamEventModal } from '@exam-center/exam-event-management';
import type { PropsWithOptionalChildren } from '@shared/utils';
import { createContext, useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import type {
  CancelExamEventArgs,
  ExamEventScheduleModalAction,
  ExamEventScheduleModalContextValue,
  ModalData,
  SetActionArgs,
} from './types';

const ExamEventScheduleModalContext = createContext<ExamEventScheduleModalContextValue | undefined>(
  undefined,
);

export default function ExamEventScheduleModalProvider(props: Readonly<PropsWithOptionalChildren>) {
  const { children } = props;
  const [modalData, setModalData] = useState<ModalData>(null);
  const [action, setAction] = useState<ExamEventScheduleModalAction | null>(null);

  const handleSetAction = useCallback(
    (args: SetActionArgs) => {
      const { action, data } = args;
      setAction(action);
      setModalData(data ?? null);
    },
    [setAction],
  );

  const handleClose = useCallback(() => {
    setAction(null);
  }, []);

  const contextValue: ExamEventScheduleModalContextValue = useMemo(() => {
    return {
      setAction: handleSetAction,
      close: handleClose,
    };
  }, [handleSetAction, handleClose]);

  return (
    <ExamEventScheduleModalContext.Provider value={contextValue}>
      {children ?? <Outlet />}
      <CancelExamEventModal
        open={action === 'cancel'}
        onClose={handleClose}
        {...(modalData as CancelExamEventArgs)}
      />
    </ExamEventScheduleModalContext.Provider>
  );
}

export { ExamEventScheduleModalContext };
